import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Calendar, TrendingUp, TrendingDown, Weight, RotateCcw, Brain, Loader2, Trophy, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PersonalBestIcon } from './PersonalBestIcon';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useProfile } from '@/hooks/useProfile';

interface SetDetails {
  reps: number;
  weight_kg: number | null;
  distance_km?: number | null;
  duration_seconds?: number | null;
}

interface ExerciseHistory {
  workout_date: string;
  workout_titles: string[];
  allSets: SetDetails[];
  workout_duration: number | null;
  workout_type?: string;
  isWeightPB?: boolean;
  isRepsPB?: boolean;
  isDistancePB?: boolean;
  isPacePB?: boolean;
  maxWeight: number;
  maxReps: number;
  totalDistance?: number;
  bestPace?: number;
}

interface ExerciseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

export const ExerciseHistoryModal = ({ isOpen, onClose, exerciseName }: ExerciseHistoryModalProps) => {
  const { user } = useAuth();
  const [history, setHistory] = useState<ExerciseHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [arnoldAnalysis, setArnoldAnalysis] = useState<string | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [personalBest, setPersonalBest] = useState<{
    maxWeight: { value: number; date: string } | null;
    maxReps: { value: number; date: string } | null;
    maxDistance: { value: number; date: string } | null;
    bestPace: { value: number; date: string } | null;
  }>({
    maxWeight: null,
    maxReps: null,
    maxDistance: null,
    bestPace: null
  });

  useEffect(() => {
    if (isOpen && exerciseName && user) {
      loadExerciseHistory();
      setArnoldAnalysis(null); // Reset analysis when opening
    }
  }, [isOpen, exerciseName, user]);

  const loadExerciseHistory = async () => {
    if (!user || !exerciseName) return;
    
    setLoading(true);
    try {
      // Get exercise history from completed workouts
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          sets,
          reps,
          weight_kg,
          distance_km,
          duration_seconds,
          set_details,
          workouts!inner(
            id,
            title,
            completed_at,
            duration_minutes,
            workout_type
          )
        `)
        .eq('name', exerciseName)
        .eq('workouts.user_id', user.id)
        .not('workouts.completed_at', 'is', null)
        .order('workouts(completed_at)', { ascending: false });

      if (error) throw error;

      // Detect if this is a running exercise
      const isRunning = !!(data && data.length > 0 && (
        data[0].workouts.workout_type === 'running' ||
        data[0].workouts.workout_type === 'endurance' ||
        exerciseName.toLowerCase().includes('running') ||
        exerciseName.toLowerCase().includes('run')
      ));

      // Group by WORKOUT (not by date) so multiple workouts in the same day stay separate
      const workoutMap = new Map<string, {
        workout_date: string;
        workout_titles: string[];
        workout_duration: number | null;
        workout_type: string;
        allSets: SetDetails[];
        maxReps: number;
        maxWeight: number;
        totalDistance: number;
        totalDuration: number;
      }>();

      (data || []).forEach((item: any) => {
        const workoutId: string | undefined = item?.workouts?.id;
        const workoutKey = workoutId || String(item?.workouts?.completed_at || '');
        if (!workoutKey) return;

        const existing = workoutMap.get(workoutKey);

        const currentReps = item.reps || 0;
        const currentWeight = item.weight_kg || 0;
        const currentDistance = item.distance_km || 0;
        const currentDuration = item.duration_seconds || 0;

        // Prefer set_details (multi-set) for strength training history
        const setsFromRecord: SetDetails[] = (!isRunning && Array.isArray(item.set_details) && item.set_details.length > 0)
          ? item.set_details.map((s: any) => ({
              reps: s?.reps || 0,
              weight_kg: (s?.weight_kg ?? s?.weight ?? null) || null,
            }))
          : [{
              reps: currentReps,
              weight_kg: currentWeight > 0 ? currentWeight : null,
              distance_km: currentDistance > 0 ? currentDistance : null,
              duration_seconds: currentDuration > 0 ? currentDuration : null,
            }];

        const recordMaxReps = Math.max(...setsFromRecord.map(s => s.reps || 0), 0);
        const recordMaxWeight = Math.max(...setsFromRecord.map(s => s.weight_kg || 0), 0);

        if (existing) {
          existing.allSets.push(...setsFromRecord);
          existing.maxReps = Math.max(existing.maxReps, recordMaxReps);
          existing.maxWeight = Math.max(existing.maxWeight, recordMaxWeight);
          existing.totalDistance += currentDistance;
          existing.totalDuration += currentDuration;
          if (!existing.workout_titles.includes(item.workouts.title)) {
            existing.workout_titles.push(item.workouts.title);
          }
        } else {
          workoutMap.set(workoutKey, {
            workout_date: item.workouts.completed_at,
            workout_titles: [item.workouts.title],
            workout_duration: item.workouts.duration_minutes,
            workout_type: item.workouts.workout_type || 'strength',
            allSets: [...setsFromRecord],
            maxReps: recordMaxReps,
            maxWeight: recordMaxWeight,
            totalDistance: currentDistance,
            totalDuration: currentDuration,
          });
        }
      });

      // Convert to array and format for display
      const formattedHistory: ExerciseHistory[] = Array.from(workoutMap.values())
        .sort((a, b) => new Date(b.workout_date).getTime() - new Date(a.workout_date).getTime())
        .slice(0, 20)
        .map(workout => ({
          workout_date: workout.workout_date,
          workout_titles: workout.workout_titles,
          allSets: workout.allSets,
          workout_duration: workout.workout_duration,
          workout_type: workout.workout_type,
          maxWeight: workout.maxWeight,
          maxReps: workout.maxReps,
          totalDistance: workout.totalDistance,
          bestPace: workout.totalDistance > 0 && workout.totalDuration > 0
            ? (workout.totalDuration / 60) / workout.totalDistance
            : 0
        }));

      // Calculate personal bests and mark PB records
      let maxWeight = { value: 0, date: '' };
      let maxReps = { value: 0, date: '' };
      let maxDistance = { value: 0, date: '' };
      let bestPace = { value: Infinity, date: '' };

      // First pass: find the actual max/best values
      formattedHistory.forEach(record => {
        if (isRunning) {
          if (record.totalDistance && record.totalDistance > maxDistance.value) {
            maxDistance = { value: record.totalDistance, date: record.workout_date };
          }
          if (record.bestPace && record.bestPace > 0 && record.bestPace < bestPace.value) {
            bestPace = { value: record.bestPace, date: record.workout_date };
          }
        } else {
          if (record.maxWeight && record.maxWeight > maxWeight.value) {
            maxWeight = { value: record.maxWeight, date: record.workout_date };
          }
          if (record.maxReps && record.maxReps > maxReps.value) {
            maxReps = { value: record.maxReps, date: record.workout_date };
          }
        }
      });

      // Second pass: mark records that match the PBs
      const historyWithPBs = formattedHistory.map(record => ({
        ...record,
        isWeightPB: !isRunning && record.maxWeight === maxWeight.value && maxWeight.value > 0,
        isRepsPB: !isRunning && record.maxReps === maxReps.value && maxReps.value > 0,
        isDistancePB: isRunning && record.totalDistance === maxDistance.value && maxDistance.value > 0,
        isPacePB: isRunning && record.bestPace === bestPace.value && bestPace.value !== Infinity
      }));

      setHistory(historyWithPBs);
      setPersonalBest({
        maxWeight: !isRunning && maxWeight.value > 0 ? maxWeight : null,
        maxReps: !isRunning && maxReps.value > 0 ? maxReps : null,
        maxDistance: isRunning && maxDistance.value > 0 ? maxDistance : null,
        bestPace: isRunning && bestPace.value !== Infinity ? bestPace : null
      });

      console.log('Personal bests calculated:', {
        isRunning,
        maxWeight: maxWeight.value > 0 ? maxWeight : null,
        maxReps: maxReps.value > 0 ? maxReps : null,
        maxDistance: maxDistance.value > 0 ? maxDistance : null,
        bestPace: bestPace.value !== Infinity ? bestPace : null,
        totalRecords: formattedHistory.length
      });
    } catch (error) {
      console.error('Error loading exercise history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceTrend = (index: number) => {
    if (index === history.length - 1) return null; // No previous record to compare
    
    const current = history[index];
    const previous = history[index + 1];
    
    const isRunning = current.workout_type === 'running' || current.workout_type === 'endurance';
    
    if (isRunning) {
      // For running: compare distance (more is better) or pace (lower is better)
      const currentDistance = current.totalDistance || 0;
      const previousDistance = previous.totalDistance || 0;
      
      if (currentDistance > previousDistance) return 'improved';
      if (currentDistance < previousDistance) return 'declined';
      
      // If distance is same, compare pace
      const currentPace = current.bestPace || Infinity;
      const previousPace = previous.bestPace || Infinity;
      
      if (currentPace < previousPace) return 'improved';
      if (currentPace > previousPace) return 'declined';
      return 'same';
    } else {
      // Calculate total volume for comparison
      const currentVolume = current.allSets.reduce((sum, set) => sum + ((set.weight_kg || 0) * set.reps), 0);
      const previousVolume = previous.allSets.reduce((sum, set) => sum + ((set.weight_kg || 0) * set.reps), 0);
      
      if (currentVolume > previousVolume) return 'improved';
      if (currentVolume < previousVolume) return 'declined';
      return 'same';
    }
  };

  const handleAskArnold = async () => {
    if (!user || !exerciseName) return;
    
    setAnalysisLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-exercise-progress', {
        body: { 
          exerciseName,
          userId: user.id
        }
      });

      // Check for rate limit error in response
      if (data?.error === 'daily_limit_reached' || data?.error === 'too_many_requests') {
        toast.error(data.message || "You've reached today's limit of 35 AI requests. Your limit will reset tomorrow.");
        return;
      }

      if (error) throw error;
      
      setArnoldAnalysis(data.analysis);
      setShowAnalysisModal(true);
    } catch (error: any) {
      console.error('Error getting Arnold analysis:', error);
      // Check if it's a rate limit error from the function response
      if (error?.message?.includes('429') || error?.message?.includes('daily_limit')) {
        toast.error("You've reached today's limit of 35 AI requests. Your limit will reset tomorrow.");
      } else {
        toast.error("Failed to get analysis. Please try again.");
      }
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <>
      {/* Arnold Analysis Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6 [&>button]:hidden">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base sm:text-xl font-bold flex items-center gap-2">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span className="truncate">Coach's Analysis</span>
            </DialogTitle>
            <p className="text-xs sm:text-sm text-muted-foreground">{exerciseName}</p>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            <Card className="bg-gradient-card border-primary/20">
              <CardContent className="p-4 sm:p-6">
                <p className="text-sm sm:text-base text-foreground leading-relaxed whitespace-pre-line">
                  {arnoldAnalysis}
                </p>
              </CardContent>
            </Card>
            <div className="flex justify-end">
              <Button onClick={() => setShowAnalysisModal(false)} size="sm" className="sm:size-default">
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Exercise History Modal */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl h-[90vh] sm:h-[85vh] flex flex-col p-4 sm:p-6 [&>button]:hidden">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base sm:text-xl font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <span className="text-sm sm:text-base truncate">Exercise History</span>
              </div>
              <Button
                onClick={handleAskArnold}
                disabled={analysisLoading || history.length === 0}
                className="bg-gradient-primary hover:bg-gradient-primary/90 text-black text-xs sm:text-sm w-full sm:w-auto"
                size="sm"
              >
                {analysisLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" />
                    <span className="text-xs sm:text-sm">Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="text-xs sm:text-sm">Analyze</span>
                  </>
                )}
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 flex flex-col gap-3 sm:gap-4">
            {/* Personal Best */}
            {(personalBest.maxWeight || personalBest.maxReps) && (
              <Card className="bg-gradient-card border-primary/20">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                    <span className="font-semibold text-foreground text-base sm:text-lg">
                      Personal Bests
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {personalBest.maxDistance && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">{personalBest.maxDistance.value.toFixed(2)}km</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">Longest Distance</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxDistance.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.bestPace && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <TrendingDown className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">{personalBest.bestPace.value.toFixed(2)}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">Best Pace (min/km)</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.bestPace.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.maxWeight && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <Weight className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">{personalBest.maxWeight.value}kg</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">Max Weight</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxWeight.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.maxReps && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <RotateCcw className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">{personalBest.maxReps.value}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">Max Reps</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxReps.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* History */}
            <ScrollArea className="flex-1 min-h-0 pr-2 sm:pr-4">
              {loading ? (
                <div className="text-center py-8 text-sm sm:text-base text-muted-foreground">
                  Loading exercise history...
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">No previous workouts found for this exercise</p>
                  <p className="text-xs sm:text-sm">This will be your first recorded session!</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-24">
                  {history.map((record, index) => {
                    const trend = getPerformanceTrend(index);
                    return (
                      <Card key={index} className="bg-background/50 border-border/50">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex justify-between items-start mb-2 sm:mb-3">
                            <div className="flex-1 min-w-0 pr-2">
                              <h4 className="font-semibold text-sm sm:text-base text-foreground flex items-center gap-1 sm:gap-2 flex-wrap">
                                <span className="truncate">{record.workout_titles.length > 1 
                                  ? `${record.workout_titles.length} workouts` 
                                  : record.workout_titles[0]}</span>
                                {record.isDistancePB && <PersonalBestIcon type="distance" className="animate-pulse flex-shrink-0" />}
                                {record.isPacePB && <PersonalBestIcon type="pace" className="animate-pulse flex-shrink-0" />}
                                {record.isWeightPB && <PersonalBestIcon type="weight" className="animate-pulse flex-shrink-0" />}
                                {record.isRepsPB && <PersonalBestIcon type="reps" className="animate-pulse flex-shrink-0" />}
                              </h4>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {formatDistanceToNow(new Date(record.workout_date), { addSuffix: true })}
                              </p>
                              <p className="text-xs text-muted-foreground hidden sm:block">
                                {new Date(record.workout_date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                              <p className="text-xs text-muted-foreground sm:hidden">
                                {new Date(record.workout_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {record.workout_duration && (
                                <Badge variant="outline" className="text-xs">
                                  {record.workout_duration} min
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Display based on exercise type */}
                          {record.workout_type === 'running' || record.workout_type === 'endurance' ? (
                            <div className="space-y-2 mt-2 sm:mt-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Running Splits:</p>
                              <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                                {record.allSets.filter(s => s.distance_km && s.distance_km > 0).map((set, setIndex) => {
                                  const pace = set.duration_seconds && set.distance_km 
                                    ? (set.duration_seconds / 60) / set.distance_km 
                                    : 0;
                                  const isPacePB = pace > 0 && pace === record.bestPace && record.isPacePB;
                                  const isDistancePB = set.distance_km === record.totalDistance && record.isDistancePB;
                                  
                                  return (
                                    <div key={setIndex} className="flex items-center justify-between bg-muted/30 rounded p-1.5 sm:p-2">
                                      <span className="text-xs sm:text-sm font-medium text-foreground">Split {setIndex + 1}</span>
                                      <div className="flex items-center gap-2 sm:gap-4">
                                        <span className="text-xs sm:text-sm text-foreground">
                                          {set.distance_km?.toFixed(2)} km
                                          {isDistancePB && (
                                            <Trophy className="inline-block h-3 w-3 ml-1 text-yellow-500" />
                                          )}
                                        </span>
                                        {pace > 0 && (
                                          <span className="text-xs sm:text-sm text-foreground">
                                            {pace.toFixed(2)} min/km
                                            {isPacePB && (
                                              <Trophy className="inline-block h-3 w-3 ml-1 text-yellow-500" />
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Running Summary Stats */}
                              <div className="grid grid-cols-3 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/30">
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Total Distance</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {record.totalDistance?.toFixed(2)} km
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Avg Pace</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {record.bestPace?.toFixed(2)} min/km
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Splits</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {record.allSets.filter(s => s.distance_km && s.distance_km > 0).length}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2 mt-2 sm:mt-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">All Sets:</p>
                              <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                                {record.allSets.map((set, setIndex) => (
                                  <div key={setIndex} className="flex items-center justify-between bg-muted/30 rounded p-1.5 sm:p-2">
                                    <span className="text-xs sm:text-sm font-medium text-foreground">Set {setIndex + 1}</span>
                                    <div className="flex items-center gap-2 sm:gap-4">
                                      <span className="text-xs sm:text-sm text-foreground">
                                        {set.reps} reps
                                        {set.reps === record.maxReps && record.isRepsPB && (
                                          <Trophy className="inline-block h-3 w-3 ml-1 text-yellow-500" />
                                        )}
                                      </span>
                                      <span className="text-xs sm:text-sm text-foreground">
                                        @ {set.weight_kg || 0} kg
                                        {set.weight_kg === record.maxWeight && record.isWeightPB && (
                                          <Trophy className="inline-block h-3 w-3 ml-1 text-yellow-500" />
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Strength Summary Stats */}
                              <div className="grid grid-cols-3 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/30">
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Total Sets</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">{record.allSets.length}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Max Weight</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">{record.maxWeight} kg</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Total Volume</p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {record.allSets.reduce((sum, set) => sum + ((set.weight_kg || 0) * set.reps), 0).toFixed(0)} kg
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {trend && (
                            <div className="mt-3 pt-3 border-t border-border/30">
                              <div className="flex items-center gap-2">
                                {trend === 'improved' && <TrendingUp className="h-4 w-4 text-green-600" />}
                                {trend === 'declined' && <TrendingDown className="h-4 w-4 text-red-600" />}
                                <Badge 
                                  variant={trend === 'improved' ? 'default' : 'secondary'}
                                  className={`text-xs ${
                                    trend === 'improved' 
                                      ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' 
                                      : trend === 'declined'
                                      ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                                      : 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800'
                                  }`}
                                >
                                  {trend === 'improved' && 'Improved Performance'}
                                  {trend === 'declined' && 'Declined Performance'}
                                  {trend === 'same' && 'Same Performance'}
                                </Badge>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            <Separator />
            
            <div className="flex justify-end">
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};